import React from 'react';

import { PinIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { IconComponent } from '@/modules/humanChat/utils';

/**
 * Props for the PinMenuContent component
 */
export interface Item {
  id: string;
  text: string;
  icon?: () => React.JSX.Element;
  onPinClick: () => void;
  onClick?: () => void;
  isPinned: boolean;
  isDisabled?: boolean;
}
interface PinMenuContentProps {
  /** Array of menu items to display */
  items: Item[];
  /** Whether to show scroll area for long lists */
  showScrollArea?: boolean;
  /** Maximum height for scroll area */
  maxHeight?: string;
}

/**
 * Generic command content component for pinning menu items
 *
 * @param props - The component props
 * @returns JSX element
 */
export const PinMenuContent = ({ items }: PinMenuContentProps) => {
  const { t } = useTranslation();

  return (
    <Command className="max-w-64">
      <CommandInput placeholder={t('common.search_dots')} />
      <CommandList className="space-y-1">
        {items.map((item) => (
          <CommandItem
            key={item.id}
            value={item.text}
            onSelect={item.onClick}
            className="flex w-full justify-between rounded-none"
          >
            <div className="flex items-center gap-2">
              {item.icon && <IconComponent icon={item.icon} />} {item.text}
            </div>
            <InfoTooltip content={item.isPinned ? t('common.unpin') : t('common.pin')} side="right">
              <Button
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  item.onPinClick();
                }}
                size="icon-xss"
                disabled={item.isDisabled}
                className="min-h-6 min-w-6"
              >
                <PinIcon
                  size={16}
                  fill={item.isPinned ? 'currentColor' : 'none'}
                  className="min-w-4"
                />
              </Button>
            </InfoTooltip>
          </CommandItem>
        ))}
        <CommandEmpty>{t('common.no_results')}</CommandEmpty>
      </CommandList>
    </Command>
  );
};
